import { useState, useEffect } from 'react';
import { OVERLAY_TYPES, TYPE_COLORS, rgbToHex, hexToRgb } from './overlayDefaults';
import { detectCategory, tempNativeUnit, tempAffine, pressureNativeUnit, pressureAffine } from '../../../utils/unitConvert';
import { gaugeChannelPatch, gaugeStylePatch, gaugeSpeedUnitPatch } from './gaugeSettings';
import styles from './LayoutEditor.module.css';

// Overlay types that display a channel value and so can carry a °C/°F selector.
const TEMP_SELECTABLE_TYPES = new Set(['numeric', 'bar', 'gauge', 'graph']);
// Same set of value-displaying types can carry a pressure (psi/bar/kPa) selector.
const PRESSURE_SELECTABLE_TYPES = new Set(['numeric', 'bar', 'gauge', 'graph']);

function GaugeSizeInput({ position, onChange }) {
  const size = Math.round(position.width * 1920);
  const [draft, setDraft] = useState(String(size));
  useEffect(() => setDraft(String(size)), [size]);
  return (
    <input type="number" className={styles.propInput} aria-label="Gauge size" min="77" max="1080" value={draft}
      onChange={e => {
        setDraft(e.target.value);
        const next = Number(e.target.value);
        if (!Number.isFinite(next) || next < 77 || next > 1080) return;
        onChange({ ...position,
          x: Math.min(position.x, 1 - next / 1920), y: Math.min(position.y, 1 - next / 1080),
          width: next / 1920, height: next / 1080,
        });
      }}
      onBlur={() => setDraft(String(size))}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
}

export default function PropertiesPanel({ overlay, availableChannels, unitPrefs, overlayStyles = {}, onUpdate, onUpdateMany, onDuplicate, onDelete }) {
  const [channelFilter, setChannelFilter] = useState('');

  // Reset filter when selected overlay changes (parent passes key={selectedId})
  useEffect(() => setChannelFilter(''), []);

  if (!overlay) {
    return (
      <div className={styles.propertiesPanel}>
        <div className={styles.propEmpty}>
          Select an overlay to edit it, or click an overlay type to add one.
        </div>
      </div>
    );
  }

  const { _id, type } = overlay;
  const typeLabel = OVERLAY_TYPES.find(t => t.value === type)?.label ?? type;
  const typeColors = TYPE_COLORS[type] || [];

  const update = (field, value) => onUpdate(_id, field, value);
  // All edits share the editor's automatic, debounced preview.
  const updateLive = update;

  // VTR exposes a style library per overlay type (gauge / bar / track_map / graph).
  // Selecting one records the style id and merges its advisory defaults into any
  // fields the user hasn't set yet (user-edited fields always win — the backend
  // treats style defaults as advisory). One batched update → one re-render.
  const styleLibrary = overlayStyles[type] || [];
  const selectedStyle = styleLibrary.find(s => s.id === overlay.style) || null;
  const applyStyle = (styleId) => {
    const style = styleLibrary.find(s => s.id === styleId);
    if (type === 'gauge') {
      onUpdateMany(_id, gaugeStylePatch(overlay, style));
      return;
    }
    const patch = { style: styleId };
    if (style?.defaults) {
      for (const [k, v] of Object.entries(style.defaults)) {
        if (overlay[k] == null) patch[k] = v;
      }
    }
    onUpdateMany ? onUpdateMany(_id, patch) : Object.entries(patch).forEach(([k, v]) => update(k, v));
  };

  const filteredChannels = channelFilter
    ? availableChannels.filter(ch => ch.name.toLowerCase().includes(channelFilter.toLowerCase()))
    : availableChannels;

  // --- Temperature unit (°C/°F) support -------------------------------------
  // A channel is temperature-capable when its native (logged) unit is a temp.
  const nativeCorF = (chName) =>
    tempNativeUnit(availableChannels.find(c => c.name === chName)?.unit);
  const channelCorF = nativeCorF(overlay.channel);
  const isTemp = channelCorF != null && TEMP_SELECTABLE_TYPES.has(type);
  // The value currently shown is whatever overlay.unit says, else the native unit.
  const currentCorF = tempNativeUnit(overlay.unit) ?? channelCorF;

  // Rewrite scale/offset/unit (the value transform) and convert any threshold /
  // axis numbers so everything stored on the overlay is already what VTR renders.
  const setTempUnit = (targetCorF, channelName = overlay.channel) => {
    const native = nativeCorF(channelName);
    if (!native) return;
    const fromDisplay = tempNativeUnit(overlay.unit) ?? native;

    // Value transform: raw (native) → target display unit.
    if (native === targetCorF) {
      update('scale', null);
      update('offset', null);
    } else {
      const va = tempAffine(native, targetCorF);
      update('scale', va.scale);
      update('offset', va.offset);
    }
    update('unit', targetCorF === 'F' ? '°F' : '°C');

    // Threshold/axis numbers are stored in the *displayed* unit, so convert them
    // from the previous display unit to the new one.
    const ta = tempAffine(fromDisplay, targetCorF);
    if (ta.scale !== 1 || ta.offset !== 0) {
      const conv = v => Math.round((v * ta.scale + ta.offset) * 10) / 10;
      const fields = type === 'graph'
        ? ['y_min', 'y_max']
        : ['min_value', 'max_value', 'warn_value', 'redline_value'];
      fields.forEach(f => { if (overlay[f] != null) update(f, conv(overlay[f])); });
    }
  };

  const tempSelector = (
    <div className={styles.propSection}>
      <label className={styles.propLabel}>Temperature unit</label>
      <select
        className={styles.propSelect}
        value={currentCorF || 'C'}
        onChange={e => setTempUnit(e.target.value)}
      >
        <option value="C">°C — Celsius</option>
        <option value="F">°F — Fahrenheit</option>
      </select>
    </div>
  );

  // --- Pressure unit (psi/bar/kPa) support ----------------------------------
  // A channel is pressure-capable when its native (logged) unit is a pressure.
  // Temp and pressure are mutually exclusive (a unit is at most one category).
  const nativePres = (chName) =>
    pressureNativeUnit(availableChannels.find(c => c.name === chName)?.unit);
  const channelPres = nativePres(overlay.channel);
  const isPressure = channelPres != null && PRESSURE_SELECTABLE_TYPES.has(type);
  // The value currently shown is whatever overlay.unit says, else the native unit.
  const currentPresUnit = pressureNativeUnit(overlay.unit) ?? channelPres;

  // Rewrite scale/offset/unit (the value transform) and convert any threshold /
  // axis numbers so everything stored on the overlay is already what VTR renders.
  const setPressureUnit = (targetUnit, channelName = overlay.channel) => {
    const native = nativePres(channelName);
    if (!native) return;
    const fromDisplay = pressureNativeUnit(overlay.unit) ?? native;

    // Value transform: raw (native) → target display unit.
    if (native === targetUnit) {
      update('scale', null);
      update('offset', null);
    } else {
      const va = pressureAffine(native, targetUnit);
      update('scale', va.scale);
      update('offset', va.offset);
    }
    update('unit', targetUnit);

    // Threshold/axis numbers are stored in the *displayed* unit, so convert them
    // from the previous display unit to the new one.
    const ta = pressureAffine(fromDisplay, targetUnit);
    if (ta.scale !== 1 || ta.offset !== 0) {
      const conv = v => Math.round((v * ta.scale + ta.offset) * 100) / 100;
      const fields = type === 'graph'
        ? ['y_min', 'y_max']
        : ['min_value', 'max_value', 'warn_value', 'redline_value'];
      fields.forEach(f => { if (overlay[f] != null) update(f, conv(overlay[f])); });
    }
  };

  const pressureSelector = (
    <div className={styles.propSection}>
      <label className={styles.propLabel}>Pressure unit</label>
      <select
        className={styles.propSelect}
        value={currentPresUnit || 'psi'}
        onChange={e => setPressureUnit(e.target.value)}
      >
        <option value="psi">psi</option>
        <option value="bar">bar</option>
        <option value="kPa">kPa</option>
      </select>
    </div>
  );

  // Panel dimensions (reference px at 1080p) — gauge, bar and graph honor these.
  // Some styles (e.g. round gauges, the vertical bar) want a particular aspect
  // ratio, so style defaults can pre-fill these; blank = VTR's per-type default.
  const panelSizeRow = (
    <div className={styles.propRow}>
      <div className={styles.propSection}>
        <label className={styles.propLabel}>Panel W (px)</label>
        <input
          type="number"
          min="0"
          className={styles.propInputSm}
          value={overlay.panel_width ?? ''}
          onChange={e => update('panel_width', e.target.value === '' ? null : parseInt(e.target.value))}
          placeholder="auto"
        />
      </div>
      <div className={styles.propSection}>
        <label className={styles.propLabel}>Panel H (px)</label>
        <input
          type="number"
          min="0"
          className={styles.propInputSm}
          value={overlay.panel_height ?? ''}
          onChange={e => update('panel_height', e.target.value === '' ? null : parseInt(e.target.value))}
          placeholder="auto"
        />
      </div>
    </div>
  );

  return (
    <div className={styles.propertiesPanel}>
      <div className={styles.propHeader}>
        <span className={styles.propTypeLabel}>{typeLabel}</span>
      </div>

      {/* Style picker — pulls VTR's per-type style library and pre-fills defaults */}
      {styleLibrary.length > 0 && (
        <div className={styles.propSection}>
          <label className={styles.propLabel}>Style</label>
          <select
            className={styles.propSelect}
            value={overlay.style || ''}
            onChange={e => applyStyle(e.target.value)}
          >
            <option value="">Classic (default)</option>
            {styleLibrary.filter(s => s.id !== 'classic').map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {selectedStyle?.description && (
            <p className={styles.propHint}>{selectedStyle.description}</p>
          )}
        </div>
      )}

      {/* Channel selector */}
      {type !== 'track_map' && (
        <div className={styles.propSection}>
          <label className={styles.propLabel}>Channel</label>
          <input
            className={styles.propInput}
            placeholder="filter channels…"
            value={channelFilter}
            onChange={e => setChannelFilter(e.target.value)}
          />
          <select
            className={styles.propSelect}
            value={overlay.channel || ''}
            onChange={e => {
              const v = e.target.value;
              if (type === 'gauge') {
                if (v !== overlay.channel) onUpdateMany(_id, gaugeChannelPatch(overlay, availableChannels.find(c => c.name === v), unitPrefs));
                return;
              }
              update('channel', v);
              if (!overlay.label && v) update('label', v);
              // A freshly-assigned temperature channel defaults to the user's
              // global temperature preference.
              if (nativeCorF(v) && TEMP_SELECTABLE_TYPES.has(type)) {
                setTempUnit(unitPrefs?.temp === 'C' ? 'C' : 'F', v);
              }
              // Likewise a pressure channel defaults to the global pressure pref.
              else if (nativePres(v) && PRESSURE_SELECTABLE_TYPES.has(type)) {
                setPressureUnit(unitPrefs?.pressure || 'psi', v);
              }
            }}
          >
            <option value="">— channel —</option>
            {filteredChannels.map(ch => (
              <option key={ch.name} value={ch.name}>
                {ch.unit ? `${ch.name} (${ch.unit})` : ch.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Label */}
      {type !== 'track_map' && type !== 'lap_table' && (
        <div className={styles.propSection}>
          <label className={styles.propLabel}>Label</label>
          <input
            className={styles.propInput}
            value={overlay.label || ''}
            onChange={e => update('label', e.target.value)}
            placeholder="Display label"
          />
        </div>
      )}

      {/* Temperature/pressure unit selector (bar / gauge / graph — numeric handles its own below) */}
      {isTemp && type !== 'numeric' && tempSelector}
      {isPressure && type !== 'numeric' && pressureSelector}

      {type === 'gauge' && !isTemp && !isPressure && (
        <div className={styles.propSection}>
          <label className={styles.propLabel}>Display unit</label>
          {detectCategory(availableChannels.find(c => c.name === overlay.channel)?.unit) === 'speed' ? (
            <select className={styles.propSelect} aria-label="Gauge speed unit" value={overlay.unit || unitPrefs?.speed || 'mph'}
              onChange={e => onUpdateMany(_id, gaugeSpeedUnitPatch(overlay, availableChannels.find(c => c.name === overlay.channel), e.target.value))}>
              <option value="mph">mph</option>
              <option value="km/h">km/h</option>
            </select>
          ) : (
            <input className={styles.propInput} aria-label="Gauge display unit" value={overlay.unit || ''}
              onChange={e => update('unit', e.target.value)} placeholder="Channel unit" />
          )}
        </div>
      )}

      {/* Gauge / Bar */}
      {(type === 'gauge' || type === 'bar') && (
        <>
          <div className={styles.propRow}>
            <div className={styles.propSection}>
              <label className={styles.propLabel}>Min</label>
              <input
                type="number"
                className={styles.propInputSm}
                value={overlay.min_value ?? ''}
                onChange={e => update('min_value', e.target.value === '' ? null : parseFloat(e.target.value))}
              />
            </div>
            <div className={styles.propSection}>
              <label className={styles.propLabel}>Max</label>
              <input
                type="number"
                className={styles.propInputSm}
                value={overlay.max_value ?? ''}
                onChange={e => update('max_value', e.target.value === '' ? null : parseFloat(e.target.value))}
              />
            </div>
          </div>
          <div className={styles.propRow}>
            <div className={styles.propSection}>
              <label className={styles.propLabel}>Warn</label>
              <input
                type="number"
                className={styles.propInputSm}
                value={overlay.warn_value ?? ''}
                onChange={e => update('warn_value', e.target.value === '' ? null : parseFloat(e.target.value))}
              />
            </div>
            <div className={styles.propSection}>
              <label className={styles.propLabel}>Redline</label>
              <input
                type="number"
                className={styles.propInputSm}
                value={overlay.redline_value ?? ''}
                onChange={e => update('redline_value', e.target.value === '' ? null : parseFloat(e.target.value))}
              />
            </div>
          </div>
          {type === 'gauge' && (
            <div className={styles.propSection}>
              <label className={styles.propLabel}>Arc span (°)</label>
              <input
                type="number"
                step="5"
                min="30"
                max="360"
                className={styles.propInput}
                value={overlay.arc_span_deg ?? ''}
                onChange={e => update('arc_span_deg', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="270"
              />
            </div>
          )}
          {type === 'gauge' ? (
            <div className={styles.propSection}>
              <label className={styles.propLabel}>Gauge size (px at 1080p)</label>
              <GaugeSizeInput position={overlay.position} onChange={next => update('position', next)} />
            </div>
          ) : panelSizeRow}
          {type === 'gauge' && (
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={overlay.show_ticks ?? true}
                onChange={e => updateLive('show_ticks', e.target.checked)}
              />
              Show ticks
            </label>
          )}
        </>
      )}

      {/* Numeric */}
      {type === 'numeric' && (
        <>
          <div className={styles.propRow}>
            <div className={styles.propSection}>
              <label className={styles.propLabel}>Decimals</label>
              <input
                type="number"
                min="0"
                max="4"
                className={styles.propInputSm}
                value={overlay.decimal_places ?? ''}
                onChange={e => update('decimal_places', e.target.value === '' ? null : parseInt(e.target.value))}
              />
            </div>
            {!isTemp && !isPressure && (
              <div className={styles.propSection}>
                <label className={styles.propLabel}>Unit</label>
                <input
                  className={styles.propInputSm}
                  value={overlay.unit || ''}
                  onChange={e => update('unit', e.target.value)}
                  placeholder="mph"
                />
              </div>
            )}
          </div>
          {isTemp && tempSelector}
          {isPressure && pressureSelector}
          {!isTemp && !isPressure && (
            <div className={styles.propSection}>
              <label className={styles.propLabel}>Scale ×</label>
              <input
                type="number"
                step="0.001"
                className={styles.propInput}
                value={overlay.scale ?? ''}
                onChange={e => update('scale', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="1.0"
              />
            </div>
          )}
          {panelSizeRow}
        </>
      )}

      {/* Lap Time */}
      {type === 'laptime' && (
        <div className={styles.propSection}>
          <label className={styles.checkLabel}>
            <input type="checkbox" checked={!!overlay.show_best} onChange={e => updateLive('show_best', e.target.checked)} />
            Show best
          </label>
          <label className={styles.checkLabel}>
            <input type="checkbox" checked={!!overlay.show_last} onChange={e => updateLive('show_last', e.target.checked)} />
            Show last
          </label>
          <label className={styles.checkLabel}>
            <input type="checkbox" checked={!!overlay.show_delta} onChange={e => updateLive('show_delta', e.target.checked)} />
            Show delta
          </label>
          <div className={styles.colorRow}>
            <label className={styles.propLabel}>Best color</label>
            <input
              type="color"
              className={styles.colorInput}
              value={overlay.best_color ? rgbToHex(overlay.best_color) : '#ffd700'}
              onChange={e => update('best_color', hexToRgb(e.target.value))}
            />
          </div>
          {panelSizeRow}
        </div>
      )}

      {/* Lap Table */}
      {type === 'lap_table' && (
        <div className={styles.propSection}>
          <label className={styles.checkLabel}>
            <input type="checkbox" checked={overlay.show_current ?? true} onChange={e => updateLive('show_current', e.target.checked)} />
            Show current lap
          </label>
          <div className={styles.propRow}>
            <label className={styles.propLabel}>Last N laps</label>
            <input
              type="number"
              min="1"
              max="20"
              className={styles.propInputSm}
              value={overlay.show_last_n ?? 5}
              onChange={e => update('show_last_n', parseInt(e.target.value) || 5)}
            />
          </div>
          <div className={styles.colorRow}>
            <label className={styles.propLabel}>Best color</label>
            <input
              type="color"
              className={styles.colorInput}
              value={overlay.best_color ? rgbToHex(overlay.best_color) : '#ffd700'}
              onChange={e => update('best_color', hexToRgb(e.target.value))}
            />
          </div>
          {panelSizeRow}
        </div>
      )}

      {/* Graph */}
      {type === 'graph' && (
        <>
          <div className={styles.propSection}>
            <label className={styles.propLabel}>Window (seconds)</label>
            <input
              type="number" step="0.5" min="1"
              className={styles.propInput}
              value={overlay.window_seconds ?? ''}
              onChange={e => update('window_seconds', e.target.value === '' ? null : parseFloat(e.target.value))}
              placeholder="10.0"
            />
          </div>
          <div className={styles.propSection}>
            <label className={styles.propLabel}>Decimals</label>
            <input
              type="number" min="0" max="4"
              className={styles.propInput}
              value={overlay.decimal_places ?? ''}
              onChange={e => update('decimal_places', e.target.value === '' ? null : parseInt(e.target.value))}
            />
          </div>
          <div className={styles.propSection}>
            <label className={styles.propLabel}>Line width (px)</label>
            <input
              type="number" step="0.5" min="0.5"
              className={styles.propInput}
              value={overlay.line_width_px ?? ''}
              onChange={e => update('line_width_px', e.target.value === '' ? null : parseFloat(e.target.value))}
              placeholder="2.0"
            />
          </div>
          <div className={styles.propSection}>
            <label className={styles.checkLabel}>
              <input type="checkbox" checked={!!overlay.fill_under_curve} onChange={e => updateLive('fill_under_curve', e.target.checked)} />
              Fill under curve
            </label>
            {overlay.fill_under_curve && (
              <div style={{ paddingTop: 4 }}>
                <label className={styles.propLabel}>Fill opacity (0–1)</label>
                <input
                  type="number" step="0.05" min="0" max="1"
                  className={styles.propInput}
                  value={overlay.fill_alpha ?? ''}
                  onChange={e => update('fill_alpha', e.target.value === '' ? null : parseFloat(e.target.value))}
                  placeholder="0.25"
                />
              </div>
            )}
          </div>
          <div className={styles.propSection}>
            <label className={styles.checkLabel}>
              <input type="checkbox" checked={overlay.show_grid ?? true} onChange={e => updateLive('show_grid', e.target.checked)} />
              Show grid
            </label>
            {overlay.show_grid && (
              <div style={{ paddingTop: 4 }}>
                <label className={styles.propLabel}>Grid divisions</label>
                <input
                  type="number" min="1" max="20"
                  className={styles.propInput}
                  value={overlay.grid_divisions ?? ''}
                  onChange={e => update('grid_divisions', e.target.value === '' ? null : parseInt(e.target.value))}
                  placeholder="4"
                />
              </div>
            )}
          </div>
          <label className={styles.checkLabel}>
            <input type="checkbox" checked={overlay.show_current_value ?? true} onChange={e => updateLive('show_current_value', e.target.checked)} />
            Show current value
          </label>
          <div className={styles.propSection}>
            <label className={styles.propLabel}>Y-axis pin (optional)</label>
            <div className={styles.propRow}>
              <div className={styles.propSection}>
                <label className={styles.propLabel}>Min</label>
                <input
                  type="number" step="any"
                  className={styles.propInputSm}
                  value={overlay.y_min ?? ''}
                  onChange={e => update('y_min', e.target.value === '' ? null : parseFloat(e.target.value))}
                  placeholder="auto"
                />
              </div>
              <div className={styles.propSection}>
                <label className={styles.propLabel}>Max</label>
                <input
                  type="number" step="any"
                  className={styles.propInputSm}
                  value={overlay.y_max ?? ''}
                  onChange={e => update('y_max', e.target.value === '' ? null : parseFloat(e.target.value))}
                  placeholder="auto"
                />
              </div>
            </div>
          </div>
          {panelSizeRow}
        </>
      )}

      {/* Track Map */}
      {type === 'track_map' && (
        <div className={styles.propSection}>
          <label className={styles.propLabel}>Tail (seconds)</label>
          <input
            type="number"
            step="0.5"
            min="0"
            className={styles.propInput}
            value={overlay.tail_seconds ?? ''}
            onChange={e => update('tail_seconds', e.target.value === '' ? null : parseFloat(e.target.value))}
            placeholder="2.0"
          />
        </div>
      )}

      {/* Font scale */}
      <div className={styles.propSection}>
        <label className={styles.propLabel}>Font scale ×</label>
        <input
          type="number"
          step="0.1"
          min="0.5"
          max="4"
          className={styles.propInput}
          value={overlay.font_size_scale ?? ''}
          onChange={e => update('font_size_scale', e.target.value === '' ? null : parseFloat(e.target.value))}
          placeholder="1.0"
        />
      </div>

      {/* Colors */}
      {typeColors.length > 0 && (
        <div className={styles.propSection}>
          <div className={styles.propLabel}>Colors</div>
          {typeColors.map(({ field, label, defaultRgb }) => (
            <label key={field} className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={overlay[field] != null}
                onChange={e => updateLive(field, e.target.checked ? defaultRgb : null)}
              />
              {label}
              {overlay[field] != null && (
                <input
                  type="color"
                  className={styles.colorInput}
                  value={rgbToHex(overlay[field])}
                  onChange={e => update(field, hexToRgb(e.target.value))}
                />
              )}
            </label>
          ))}
        </div>
      )}

      <div className={styles.propDivider} />
      <button className={styles.duplicateBtn} onClick={() => onDuplicate(_id)} title="Duplicate (Ctrl/Cmd+D)">
        Duplicate Overlay
      </button>
      <button className={styles.deletePropBtn} onClick={() => onDelete(_id)}>
        Delete Overlay
      </button>
    </div>
  );
}
